import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Check, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const pollRef = useRef(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('lifelink_token') : null;
  const headers = { Authorization: `Bearer ${token}` };

  const fetchNotifs = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/notifications`, { headers });
      setNotifications(data.notifications || []);
      setUnread(data.unread || 0);
    } catch (e) { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    fetchNotifs();
    pollRef.current = setInterval(fetchNotifs, 30000);
    return () => clearInterval(pollRef.current);
  }, [fetchNotifs]);

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { headers });
      fetchNotifs();
    } catch (e) { /* ignore */ }
  };

  if (!user) return null;

  return (
    <div className="relative" data-testid="notification-bell-container">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-[#4A6B8A] hover:text-[#FF3B30] transition-all"
        data-testid="notification-bell-btn"
        title="Notifiche"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-[#FF3B30] text-white text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full" data-testid="notif-unread-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-[#B8D4E8] z-50 max-h-[500px] flex flex-col" data-testid="notif-dropdown">
          <div className="flex items-center justify-between p-3 border-b border-[#B8D4E8] bg-[#F0F7FC]">
            <span className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">Notifiche</span>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAllRead} className="flex items-center gap-1 text-[10px] text-[#0A84FF] uppercase tracking-widest" data-testid="notif-mark-read-btn">
                  <Check size={12} /> Segna tutte lette
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-[#4A6B8A] hover:text-[#FF3B30]"><X size={14} /></button>
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-xs text-[#6B8AB5]">Nessuna notifica</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`p-3 border-b border-[#E8F4FD] ${!n.letto ? 'bg-[#FF3B30]/5 border-l-2 border-l-[#FF3B30]' : 'bg-white'}`}
                  data-testid={`notif-item-${n.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-[#0D1B2A]">{n.titolo}</div>
                      <div className="text-[11px] text-[#4A6B8A] mt-0.5">{n.messaggio}</div>
                      <div className="text-[9px] text-[#B8D4E8] mt-1 uppercase tracking-widest">
                        {new Date(n.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    {!n.letto && <span className="w-2 h-2 rounded-full bg-[#FF3B30] flex-shrink-0 mt-1" />}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
